/**
 * Genetics record types. Each one is a fact someone can say out loud; the summary is the sentence
 * they check before filing. Planned matings and orders live in jobs, not here.
 */
import { addDays, defineEvent, f, msg, type Formatters, type Issue, type Money, type StockEffect, type SubjectRef } from "../../../contract";

const ROLE = { sire: "Sire", dam: "Dam (genetic)" } as const;
const BASIS = { dna: "DNA test", association: "Association record", recorded: "Ranch records" } as const;
export const SAMPLE_TYPES = { hair: "Hair", blood: "Blood card", tissue: "Tissue (TSU)", semen: "Semen" } as const;
export const DNA_TESTS = { parentage: "Parentage", genomic: "Genomic EPDs", conditions: "Genetic conditions", coat: "Coat color or polled", other: "Other test" } as const;
export const PARENT_OUTCOMES = { verified: "Verified", excluded: "Excluded", no_profile: "Parent not on file", not_tested: "Not tested", inconclusive: "Inconclusive" } as const;
export const CONDITION_RESULTS = { free: "Tested free", carrier: "Carrier", affected: "Affected (two copies)", inconclusive: "Inconclusive" } as const;
export const REGISTRATION_STATUS = { submitted: "Submitted", registered: "Registered", rejected: "Rejected", pending: "On hold", recorded: "Recorded, not registered" } as const;
export const EMBRYO_GRADES = { "1": "Grade 1 (excellent or good)", "2": "Grade 2 (fair)", "3": "Grade 3 (poor)" } as const;
export const EMBRYO_STAGES = {
  "4": "Stage 4 (morula)", "5": "Stage 5 (early blastocyst)", "6": "Stage 6 (blastocyst)", "7": "Stage 7 (expanded blastocyst)", "8": "Stage 8 (hatched blastocyst)",
} as const;
export const INVENTORY_ACTIONS = {
  moved: "Moved", sold: "Sold", shipped: "Shipped out", discarded: "Broken or thrown out", counted: "Counted", returned: "Returned to us",
} as const;
export const CG_STAGES = { birth: "Birth", weaning: "Weaning", yearling: "Yearling" } as const;

const subject = (subjects: SubjectRef[], role: string) => subjects.find((s) => s.role === role);
const roleWord = (x: Formatters, role: string | undefined) => (role === "dam" ? x.t("dam") : x.t("sire"));
const basisWord = (x: Formatters, basis: string | undefined) => x.t(basis === "dna" ? "DNA" : basis === "association" ? "association" : "ranch records");

// ---------------------------------------------------------------- pedigree

export interface ParentageData {
  role: "sire" | "dam"; parent_id: string; status: "confirmed" | "excluded" | "suspected"; basis: "dna" | "association" | "recorded";
  lab?: string; report?: string; note?: string;
}

export const parentage = defineEvent<ParentageData>({
  type: "genetics.parentage",
  label: "Parentage finding",
  description: "A sire or dam confirmed, excluded or suspected, with the evidence behind it.",
  icon: "pedigree",
  tone: "genetics",
  subjects: [{ role: "animal", kind: "animal", label: "Calf or animal", required: true }],
  fields: {
    role: f.enum("Parent", ROLE, { required: true, help: "For an ET calf the genetic dam is the donor, not the recipient." }),
    parent_id: f.ref("Which animal", "animal", { required: true, agent: "The candidate parent. Use find_animals; a reference sire is fine." }),
    status: f.enum("Finding", { confirmed: "Confirmed", excluded: "Excluded", suspected: "Suspected" }, { required: true, agent: "suspected for a likely sire from a multi-sire pasture or exposure dates; confirmed or excluded only with a DNA report or association record" }),
    basis: f.enum("Based on", BASIS, { required: true }),
    lab: f.text("Lab or association", { level: "more" }),
    report: f.text("Report or case number", { level: "more" }),
    note: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const values = { parent: x.label(e.data.parent_id), role: roleWord(x, e.data.role), animal: subject(e.subjects, "animal")?.label ?? "", basis: basisWord(x, e.data.basis) };
    if (e.data.status === "confirmed") return x.t("{{parent}} confirmed as {{role}} of {{animal}} ({{basis}})", values);
    if (e.data.status === "excluded") return x.t("{{parent}} excluded as {{role}} of {{animal}} ({{basis}})", values);
    return x.t("{{parent}} may be the {{role}} of {{animal}} ({{basis}})", values);
  },
  validate: (d, subjects) => (subjects.some((s) => s.role === "animal" && s.id === d.parent_id) ? [{ path: "data.parent_id", message: "An animal can't be its own parent." }] : []),
  agent: {
    when: "Evidence about who an animal's sire or dam is: a DNA parentage result, an association record, or a likely sire from exposure.",
    avoid: "To actually change the recorded sire or dam use genetics.pedigree_correction as well. Not for calving (breeding.calving) or a raw lab report (genetics.dna_result).",
    examples: ["DNA confirms B12 is the sire of 640", "the lab excluded Ranger as sire of 640", "640 is probably out of B20, he was the only bull in North"],
  },
});

export interface PedigreeCorrectionData { role: "sire" | "dam"; parent_id?: string; previous_id?: string; basis: string; reason: string }

export const pedigreeCorrection = defineEvent<PedigreeCorrectionData>({
  type: "genetics.pedigree_correction",
  label: "Pedigree correction",
  description: "Change the recorded sire or dam, keeping the old link in history.",
  icon: "pedigree",
  tone: "genetics",
  review: "always",
  permission: "record",
  subjects: [{ role: "animal", kind: "animal", label: "Animal", required: true }],
  fields: {
    role: f.enum("Parent", ROLE, { required: true }),
    parent_id: f.ref("Correct parent", "animal", { help: "Leave empty to mark the parent unknown.", agent: "The correct parent. Omit only when they say the parent is unknown." }),
    previous_id: f.ref("Was recorded as", "animal", { level: "more", agent: "The currently recorded parent from get_animal, when there is one." }),
    basis: f.enum("Based on", { ...BASIS, mistake: "Typing mistake" }, { required: true }),
    reason: f.longText("Why", { required: true, agent: "In the speaker's words" }),
  },
  summary: (e, x) => {
    const values = { animal: subject(e.subjects, "animal")?.label ?? "", role: roleWord(x, e.data.role), parent: x.label(e.data.parent_id), previous: x.label(e.data.previous_id) };
    if (!e.data.parent_id) return x.t("{{animal}}: {{role}} set to unknown", values);
    if (e.data.previous_id) return x.t("{{animal}}: {{role}} changed from {{previous}} to {{parent}}", values);
    return x.t("{{animal}}: {{role}} set to {{parent}}", values);
  },
  validate: (d, subjects) => (d.parent_id && subjects.some((s) => s.id === d.parent_id) ? [{ path: "data.parent_id", message: "An animal can't be its own parent." }] : []),
  agent: {
    when: "Someone says an animal's recorded sire or dam is wrong and gives the right one (or says it is unknown).",
    avoid: "Not for a new calf's parents (breeding.calving) or a nurse or recipient cow (breeding).",
    examples: ["640's sire is actually B20, not B12", "the papers say 812P is out of 703D11", "we don't know who 58's dam is"],
  },
});

export interface CompositionData { components: Array<{ breed: string; percent: number }>; basis?: string; note?: string }

export const breedComposition = defineEvent<CompositionData>({
  type: "genetics.breed_composition",
  label: "Breed makeup",
  description: "The breeds that make up an animal, by percent. What isn't known stays unknown.",
  icon: "layers",
  tone: "genetics",
  subjects: [{ role: "animal", kind: "animal", label: "Animals", required: true, many: true }],
  fields: {
    components: f.list("Breeds", f.object("Breed", {
      breed: f.text("Breed", { required: true }),
      percent: f.number("Percent", { required: true, min: 0.1, max: 100, suffix: "%" }),
    }), { required: true, itemLabel: "Breed", agent: "Only the parts they said. Leave the rest out; it shows as unknown. Never round up to 100." }),
    basis: f.enum("Source", { reported: "Seller or owner said", registry: "Association papers", parents: "Worked out from parents", genomic: "Genomic test" }, { level: "more" }),
    note: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const parts = (e.data.components ?? []).map((c) => `${Number(c.percent.toFixed(2))}% ${c.breed}`);
    const total = (e.data.components ?? []).reduce((n, c) => n + c.percent, 0);
    if (total < 99.95) parts.push(x.t("{{percent}}% unknown", { percent: Number((100 - total).toFixed(2)) }));
    return x.list(parts);
  },
  validate: (d) => {
    const issues: Issue[] = [];
    const total = (d.components ?? []).reduce((n, c) => n + c.percent, 0);
    if (total > 100.01) issues.push({ path: "data.components", message: "Breeds add up to more than 100%." });
    const names = (d.components ?? []).map((c) => c.breed.trim().toLowerCase());
    if (new Set(names).size !== names.length) issues.push({ path: "data.components", message: "List each breed once." });
    return issues;
  },
  agent: {
    when: "Breed makeup of an animal by percent: half Angus, quarter Hereford, purebred Red Angus.",
    examples: ["58 is half Angus, quarter Hereford, rest unknown", "the heifers are purebred Red Angus"],
  },
});

// ---------------------------------------------------------------- evaluations

export interface EvaluationData {
  association: string; kind?: "epd" | "ebv" | "genomic"; release?: string; genomic?: boolean; source?: "entered" | "imported";
  traits?: Array<{ trait: string; value: number; acc?: number; pct?: number }>;
  indexes?: Array<{ index: string; value: number; pct?: number }>;
  note?: string;
}

export const evaluation = defineEvent<EvaluationData>({
  type: "genetics.evaluation",
  label: "EPDs",
  description: "A snapshot of EPDs or EBVs from one association or lab run, with accuracy and percentile ranks.",
  icon: "report",
  tone: "genetics",
  subjects: [{ role: "animal", kind: "animal", label: "Animal", required: true }],
  fields: {
    association: f.text("Association or lab", { required: true, placeholder: "Red Angus (RAAA)", agent: "Who published the numbers: RAAA, AAA, AHA, ASA, or a lab such as Neogen" }),
    traits: f.list("EPDs", f.object("EPD", {
      trait: f.text("Trait", { required: true, agent: "Trait code as printed: CED, BW, WW, YW, Milk, CEM, HPG, Stay, MW, DMI, CW, Marb, RE, Fat, SC, Doc" }),
      value: f.number("Value", { required: true }),
      acc: f.number("Accuracy", { min: 0, max: 1, help: "As printed, 0 to 1." }),
      pct: f.number("Top %", { min: 0, max: 100, help: "Percentile rank: 10 means top 10%." }),
    }), { itemLabel: "EPD", agent: "Only traits that were stated. A blank is not zero." }),
    indexes: f.list("Indexes", f.object("Index", {
      index: f.text("Index", { required: true, agent: "HB, GM, $M, $W, $B, $C..." }),
      value: f.number("Value", { required: true }),
      pct: f.number("Top %", { min: 0, max: 100 }),
    }), { level: "more", itemLabel: "Index" }),
    kind: f.enum("Type", { epd: "EPD", ebv: "EBV", genomic: "Genomic score" }, { level: "more" }),
    release: f.text("Evaluation run", { level: "more", placeholder: "Fall 2026" }),
    genomic: f.bool("Genomic-enhanced", { level: "more" }),
    source: f.enum("Entered from", { entered: "Typed in", imported: "Imported file" }, { level: "more" }),
    note: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const shown = (e.data.traits ?? []).slice(0, 4).map((t) => `${t.trait} ${t.value > 0 ? "+" : ""}${t.value}`);
    const more = (e.data.traits ?? []).length + (e.data.indexes ?? []).length - shown.length;
    const head = x.t("{{association}} EPDs", { association: e.data.association });
    const list = shown.length ? `${head}: ${x.list(shown)}` : head;
    return more > 0 ? `${list} ${x.t("and {{count}} more", { count: more })}` : list;
  },
  validate: (d) => {
    const issues: Issue[] = [];
    if (!(d.traits?.length) && !(d.indexes?.length)) issues.push({ path: "data.traits", message: "Add at least one EPD or index." });
    const keys = [...(d.traits ?? []).map((t) => t.trait.trim().toUpperCase()), ...(d.indexes ?? []).map((t) => t.index.trim().toUpperCase())];
    if (new Set(keys).size !== keys.length) issues.push({ path: "data.traits", message: "Each trait appears once per run." });
    return issues;
  },
  agent: {
    when: "EPD or EBV numbers for an animal from an association or genomic lab, usually read off papers, a catalog or a website.",
    avoid: "Not for actual weights or scores measured on the ranch (performance).",
    examples: ["B12's new Red Angus EPDs: CED 12, BW minus 1.2, WW 68, YW 112", "Inspiration's fall EPDs came out, HB 152"],
  },
});

// ---------------------------------------------------------------- DNA

export interface DnaSampleData { sample_type?: keyof typeof SAMPLE_TYPES; barcode?: string; lab?: string; tests?: Array<keyof typeof DNA_TESTS>; candidates?: string[]; note?: string }

export const dnaSample = defineEvent<DnaSampleData>({
  type: "genetics.dna_sample",
  label: "DNA sample",
  description: "A hair, blood or tissue sample pulled for DNA testing. Collected is not the same as sent.",
  icon: "dna",
  tone: "genetics",
  subjects: [{ role: "animal", kind: "animal", label: "Animal", required: true }],
  fields: {
    sample_type: f.enum("Sample", SAMPLE_TYPES),
    barcode: f.text("Barcode or sample ID", { agent: "Exactly as printed. Keep leading zeros. Omit when not said." }),
    lab: f.text("Lab", { placeholder: "Neogen" }),
    tests: f.multiEnum("Tests wanted", DNA_TESTS, { level: "more" }),
    candidates: f.refs("Possible sires", "animal", { level: "more", help: "Bulls the lab should check for parentage." }),
    note: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const type = e.data.sample_type ? x.t(SAMPLE_TYPES[e.data.sample_type]) : x.t("DNA");
    const parts = [x.t("{{type}} sample", { type })];
    if (e.data.barcode) parts.push(x.t("barcode {{barcode}}", { barcode: e.data.barcode }));
    if (e.data.tests?.length) parts.push(x.t("for {{tests}}", { tests: x.list(e.data.tests.map((t) => x.t(DNA_TESTS[t]).toLowerCase())) }));
    if (e.data.lab) parts.push(x.t("to {{lab}}", { lab: e.data.lab }));
    return parts.join(", ");
  },
  agent: {
    when: "A DNA sample (hair, blood card, tissue punch) was pulled from an animal. One record per animal, so each can carry its own barcode.",
    avoid: "Not for mailing samples (genetics.dna_sent) or results (genetics.dna_result).",
    examples: ["pulled hair on the 2025 calves for DNA", "tissue on 541, barcode 00082931, for parentage"],
  },
});

export interface DnaSentData { lab: string; order_number?: string; tests?: Array<keyof typeof DNA_TESTS> }

export const dnaSent = defineEvent<DnaSentData>({
  type: "genetics.dna_sent",
  label: "DNA samples sent",
  description: "Samples mailed or handed to a lab.",
  icon: "send",
  tone: "genetics",
  subjects: [{ role: "animal", kind: "animal", label: "Animals", required: true, many: true }],
  fields: {
    lab: f.text("Lab", { required: true }),
    order_number: f.text("Order or case number"),
    tests: f.multiEnum("Tests ordered", DNA_TESTS, { level: "more" }),
  },
  summary: (e, x) => {
    const n = e.subjects.filter((s) => s.role === "animal").length;
    const base = x.count(n, "Sent {{count}} DNA sample", "Sent {{count}} DNA samples");
    return [base, x.t("to {{lab}}", { lab: e.data.lab }), e.data.order_number ? x.t("order {{number}}", { number: e.data.order_number }) : ""].filter(Boolean).join(" ");
  },
  agent: {
    when: "DNA samples were mailed, shipped or dropped off at a lab.",
    examples: ["mailed the hair cards to Neogen today, order 55102"],
  },
});

export interface DnaResultData {
  lab: string; outcome: "complete" | "partial" | "failed"; barcode?: string;
  sire?: keyof typeof PARENT_OUTCOMES; sire_tested_id?: string; dam?: keyof typeof PARENT_OUTCOMES; dam_tested_id?: string;
  genomic?: boolean; ge_reference?: string; call_rate?: number;
  conditions?: Array<{ condition: string; result: keyof typeof CONDITION_RESULTS }>;
  report?: string; revised?: boolean; note?: string;
}

export const dnaResult = defineEvent<DnaResultData>({
  type: "genetics.dna_result",
  label: "DNA results",
  description: "What the lab reported for one animal: parentage, genomic EPDs, call rate and genetic conditions.",
  icon: "lab",
  tone: "genetics",
  subjects: [{ role: "animal", kind: "animal", label: "Animal", required: true }],
  fields: {
    lab: f.text("Lab", { required: true }),
    outcome: f.enum("Sample", { complete: "Results back", partial: "Some tests failed", failed: "Sample failed, collect again" }, { required: true }),
    sire: f.enum("Sire check", PARENT_OUTCOMES),
    sire_tested_id: f.ref("Sire tested", "animal", { level: "more" }),
    dam: f.enum("Dam check", PARENT_OUTCOMES, { level: "more" }),
    dam_tested_id: f.ref("Dam tested", "animal", { level: "more" }),
    genomic: f.bool("Genomic EPDs released", { level: "more" }),
    ge_reference: f.text("Genomic EPD reference", { level: "more", help: "The run or report the genomic-enhanced EPDs came from." }),
    call_rate: f.number("Call rate", { level: "more", min: 0, max: 100, suffix: "%" }),
    conditions: f.list("Genetic conditions", f.object("Condition", {
      condition: f.text("Condition", { required: true, agent: "Code or name as reported: AM, NH, CA, DD, OS, PHA, Polled..." }),
      result: f.enum("Result", CONDITION_RESULTS, { required: true }),
    }), { level: "more", itemLabel: "Condition" }),
    barcode: f.text("Barcode or sample ID", { level: "more" }),
    report: f.text("Report number", { level: "more" }),
    revised: f.bool("Corrected report", { level: "more", help: "The lab sent a corrected version of an earlier report." }),
    note: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    if (e.data.outcome === "failed") return x.t("{{lab}}: sample failed, collect again", { lab: e.data.lab });
    const parts: string[] = [];
    const outcome = (role: "sire" | "dam", v: string | undefined, id: string | undefined) => {
      if (!v || v === "not_tested") return;
      const who = id ? x.label(id) : roleWord(x, role);
      if (v === "verified") parts.push(x.t("{{who}} verified as {{role}}", { who, role: roleWord(x, role) }));
      else if (v === "excluded") parts.push(x.t("{{who}} excluded as {{role}}", { who, role: roleWord(x, role) }));
      else if (v === "no_profile") parts.push(x.t("{{role}} not on file at the lab", { role: roleWord(x, role) }));
      else parts.push(x.t("{{role}} check inconclusive", { role: roleWord(x, role) }));
    };
    outcome("sire", e.data.sire, e.data.sire_tested_id);
    outcome("dam", e.data.dam, e.data.dam_tested_id);
    if (e.data.genomic) parts.push(x.t("genomic EPDs"));
    if (e.data.call_rate !== undefined) parts.push(x.t("call rate {{rate}}%", { rate: e.data.call_rate }));
    const flagged = (e.data.conditions ?? []).filter((c) => c.result === "carrier" || c.result === "affected");
    for (const c of flagged) parts.push(x.t("{{condition}} {{result}}", { condition: c.condition, result: x.t(CONDITION_RESULTS[c.result]).toLowerCase() }));
    if ((e.data.conditions ?? []).length && !flagged.length) parts.push(x.t("conditions tested free"));
    if (e.data.outcome === "partial") parts.push(x.t("some tests failed"));
    return `${e.data.lab}: ${parts.length ? x.list(parts) : x.t("results back")}`;
  },
  effects: {
    followUps: (d, on, subjects, today) => {
      const animal = subjects.find((s) => s.role === "animal");
      if (d.outcome !== "failed" || !animal) return [];
      return [{ key: "recollect", title: msg("Pull a new DNA sample"), due_at: addDays(on ?? today, 14), subject: { kind: "animal", id: animal.id } }];
    },
  },
  agent: {
    when: "A lab reported DNA results for an animal: parentage verified or excluded, genomic EPDs, call rate, carrier status, or a failed sample.",
    avoid: "Use genetics.evaluation for the EPD numbers themselves and genetics.pedigree_correction to change a recorded parent.",
    examples: ["Neogen says B12 is excluded as sire of 640", "541's DNA came back, verified to B20, genomic EPDs out", "hair on 22 failed, need to resample"],
  },
});

// ---------------------------------------------------------------- registration

export interface RegistrationData { association: string; status: keyof typeof REGISTRATION_STATUS; number?: string; name?: string; reason?: string; reference?: string }

export const registration = defineEvent<RegistrationData>({
  type: "genetics.registration",
  label: "Registration",
  description: "A registration submitted to an association, and what came back: a number, a hold or a rejection.",
  icon: "award",
  tone: "genetics",
  review: "always",
  subjects: [{ role: "animal", kind: "animal", label: "Animal", required: true }],
  fields: {
    association: f.text("Association", { required: true, placeholder: "Red Angus (RAAA)" }),
    status: f.enum("Status", REGISTRATION_STATUS, { required: true }),
    number: f.text("Registration number", { agent: "Exactly as issued, with any prefix and leading zeros." }),
    reason: f.text("Reason", { help: "Why it was rejected or held." }),
    name: f.text("Registered name", { level: "more" }),
    reference: f.text("Batch or job number", { level: "more" }),
  },
  summary: (e, x) => {
    const a = e.data.association;
    switch (e.data.status) {
      case "registered": return e.data.number ? x.t("Registered with {{association}}: {{number}}", { association: a, number: e.data.number }) : x.t("Registered with {{association}}", { association: a });
      case "submitted": return x.t("Sent to {{association}} for registration", { association: a });
      case "rejected": return e.data.reason ? x.t("{{association}} rejected the registration: {{reason}}", { association: a, reason: e.data.reason }) : x.t("{{association}} rejected the registration", { association: a });
      case "pending": return e.data.reason ? x.t("{{association}} registration on hold: {{reason}}", { association: a, reason: e.data.reason }) : x.t("{{association}} registration on hold", { association: a });
      default: return e.data.number ? x.t("Recorded with {{association}} ({{number}}), not registered", { association: a, number: e.data.number }) : x.t("Recorded with {{association}}, not registered", { association: a });
    }
  },
  validate: (d) => (d.status === "registered" && !d.number?.trim() ? [{ path: "data.number", message: "A registered animal needs its registration number." }] : []),
  effects: {
    followUps: (d, on, subjects, today) => {
      const animal = subjects.find((s) => s.role === "animal");
      if (d.status !== "rejected" || !animal) return [];
      return [{ key: "rejected", title: msg("Fix the {{association}} registration", { association: d.association }), due_at: addDays(on ?? today, 7), subject: { kind: "animal", id: animal.id } }];
    },
  },
  agent: {
    when: "An animal was submitted for registration, or the association sent back a registration number, a hold or a rejection.",
    avoid: "Preparing a batch of calves is a job (genetics.registration), not this event. A sale transfer is genetics.registration_transfer.",
    examples: ["got the Red Angus registration numbers back: 812P is 4012345", "RAAA rejected 31Q, sire not registered", "B31 is registered, number 3987001"],
  },
});

export interface TransferData { association: string; status: "prepared" | "submitted" | "completed"; certificate?: string; retained?: boolean; note?: string }

export const registrationTransfer = defineEvent<TransferData>({
  type: "genetics.registration_transfer",
  label: "Registration transfer",
  description: "Transferring a registered animal's papers to a buyer.",
  icon: "handshake",
  tone: "genetics",
  subjects: [
    { role: "animal", kind: "animal", label: "Animal", required: true },
    { role: "buyer", kind: "contact", label: "Buyer", required: true },
  ],
  fields: {
    association: f.text("Association", { required: true }),
    status: f.enum("Status", { prepared: "Paperwork ready", submitted: "Sent to association", completed: "Transferred" }, { required: true }),
    certificate: f.text("Certificate number", { level: "more" }),
    retained: f.bool("We keep part interest", { level: "more" }),
    note: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const buyer = subject(e.subjects, "buyer")?.label ?? "";
    const status = e.data.status === "completed" ? x.t("transferred") : e.data.status === "submitted" ? x.t("sent to association") : x.t("paperwork ready");
    return x.t("Registration transfer to {{buyer}} ({{association}}): {{status}}", { buyer, association: e.data.association, status });
  },
  agent: {
    when: "Registration papers of a sold animal are being or were transferred to the buyer.",
    examples: ["sent the transfer on 4051 to Red Angus for Bob Jones"],
  },
});

// ---------------------------------------------------------------- contemporary groups

export interface ContemporaryData { stage: keyof typeof CG_STAGES; season: string; code: string; basis?: string; adjustments?: Array<{ animal_id: string; reason: string }> }

export const contemporaryGroup = defineEvent<ContemporaryData>({
  type: "genetics.contemporary_group",
  label: "Contemporary group",
  description: "Calves that had the same chance to perform, grouped for fair comparison and association reports.",
  icon: "group",
  tone: "genetics",
  subjects: [
    { role: "animal", kind: "animal", label: "Calves", required: true, many: true },
    { role: "group", kind: "group", label: "Group" },
  ],
  fields: {
    stage: f.enum("Stage", CG_STAGES, { required: true }),
    season: f.text("Season", { required: true, placeholder: "2026" }),
    code: f.text("Group code", { required: true, placeholder: "A" }),
    basis: f.text("How grouped", { level: "more" }),
    adjustments: f.list("Moved by hand", f.object("Moved calf", {
      animal_id: f.ref("Calf", "animal", { required: true }),
      reason: f.text("Reason", { required: true }),
    }), { level: "more", itemLabel: "Moved calf" }),
  },
  summary: (e, x) => {
    const n = e.subjects.filter((s) => s.role === "animal").length;
    const base = x.t("{{season}} {{stage}} group {{code}}: {{calves}}", {
      season: e.data.season, stage: x.t(CG_STAGES[e.data.stage] ?? e.data.stage).toLowerCase(), code: e.data.code, calves: x.count(n, "{{count}} calf", "{{count}} calves"),
    });
    const moved = e.data.adjustments?.length ?? 0;
    return moved ? `${base} (${x.count(moved, "{{count}} moved by hand", "{{count}} moved by hand")})` : base;
  },
  validate: (d, subjects) => {
    const ids = new Set(subjects.filter((s) => s.role === "animal").map((s) => s.id));
    return (d.adjustments ?? []).filter((a) => !ids.has(a.animal_id)).map((a, i) => ({ path: `data.adjustments.${i}.animal_id`, message: "A moved calf must be in the group." }));
  },
  agent: {
    when: "Setting which calves were raised together for fair comparison (contemporary group) at birth, weaning or yearling.",
    avoid: "Management differences alone (creep feed, a sick calf) are notes or health records, not groups.",
    examples: ["use the fall bulls as one contemporary group for weaning"],
  },
});

// ---------------------------------------------------------------- flushes, collections and inventory

export interface FlushData {
  method?: "conventional" | "ivf"; recovered?: number; usable?: number; degenerate?: number; unfertilized?: number; frozen?: number; fresh?: number; discarded?: number;
  lots?: Array<{ lot_id: string; count: number; grade?: keyof typeof EMBRYO_GRADES; stage?: keyof typeof EMBRYO_STAGES }>;
  semen_lot_id?: string; semen_straws?: number; provider_id?: string; provider_ref?: string; protocol?: string; note?: string;
}

export const flush = defineEvent<FlushData>({
  type: "genetics.flush",
  label: "Flush",
  description: "A donor cow flushed for embryos: what was recovered, frozen, transferred fresh or discarded.",
  icon: "embryo",
  tone: "genetics",
  review: "always",
  subjects: [
    { role: "donor", kind: "animal", label: "Donor", required: true },
    { role: "sire", kind: "animal", label: "Sire" },
  ],
  fields: {
    recovered: f.int("Recovered", { min: 0 }),
    usable: f.int("Usable", { min: 0, help: "Good enough to transfer or freeze." }),
    frozen: f.int("Frozen", { min: 0 }),
    fresh: f.int("Transferred fresh", { min: 0, level: "more" }),
    method: f.enum("Method", { conventional: "Conventional flush", ivf: "IVF (OPU)" }, { level: "more" }),
    degenerate: f.int("Degenerate", { min: 0, level: "more" }),
    unfertilized: f.int("Unfertilized", { min: 0, level: "more" }),
    discarded: f.int("Discarded", { min: 0, level: "more" }),
    lots: f.list("Into the tank", f.object("Embryo lot", {
      lot_id: f.ref("Lot", "stock_lot", { required: true, filter: { kind: "embryo" } }),
      count: f.int("Embryos", { required: true, min: 1 }),
      grade: f.enum("Grade", EMBRYO_GRADES),
      stage: f.enum("Stage", EMBRYO_STAGES),
    }), { level: "more", itemLabel: "Embryo lot" }),
    semen_lot_id: f.ref("Semen used", "stock_lot", { level: "more", filter: { kind: "semen" } }),
    semen_straws: f.int("Straws used", { level: "more", min: 0 }),
    provider_id: f.ref("Vet or lab", "contact", { level: "more" }),
    provider_ref: f.text("Their reference", { level: "more" }),
    protocol: f.text("Protocol", { level: "more" }),
    note: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const donor = subject(e.subjects, "donor")?.label ?? "";
    const sire = subject(e.subjects, "sire")?.label;
    const head = sire ? x.t("Flushed {{donor}} to {{sire}}", { donor, sire }) : x.t("Flushed {{donor}}", { donor });
    const parts: string[] = [];
    if (e.data.recovered !== undefined) parts.push(x.t("{{count}} recovered", { count: e.data.recovered }));
    if (e.data.usable !== undefined) parts.push(x.t("{{count}} usable", { count: e.data.usable }));
    if (e.data.frozen !== undefined) parts.push(x.t("{{count}} frozen", { count: e.data.frozen }));
    if (e.data.fresh) parts.push(x.t("{{count}} transferred fresh", { count: e.data.fresh }));
    return parts.length ? `${head}: ${x.list(parts)}` : head;
  },
  validate: (d, subjects) => {
    const issues: Issue[] = [];
    if (d.recovered !== undefined && d.usable !== undefined && d.usable > d.recovered) issues.push({ path: "data.usable", message: "Usable can't be more than recovered." });
    if (d.usable !== undefined && (d.frozen ?? 0) + (d.fresh ?? 0) > d.usable) issues.push({ path: "data.frozen", message: "Frozen and fresh can't add up to more than usable." });
    const inLots = (d.lots ?? []).reduce((n, l) => n + l.count, 0);
    if (d.frozen !== undefined && inLots > d.frozen) issues.push({ path: "data.lots", message: "More embryos in the tank than were frozen." });
    if (d.semen_straws && !d.semen_lot_id) issues.push({ path: "data.semen_lot_id", message: "Pick the semen lot the straws came from." });
    const donor = subject(subjects, "donor");
    const sire = subject(subjects, "sire");
    if (donor && sire && donor.id === sire.id) issues.push({ path: "subjects.sire", message: "The sire can't be the donor." });
    return issues;
  },
  effects: {
    stock: (d) => {
      const out: StockEffect[] = (d.lots ?? []).map((l) => ({ lot_id: l.lot_id, delta: l.count, unit: "embryo", reason: "receive" }));
      if (d.semen_lot_id && d.semen_straws) out.push({ lot_id: d.semen_lot_id, delta: -d.semen_straws, unit: "straw", reason: "use" });
      return out;
    },
  },
  agent: {
    when: "A donor cow was flushed (or OPU/IVF) and embryos were recovered, frozen or transferred.",
    avoid: "Putting an embryo into a recipient cow is breeding's embryo transfer. Frozen embryos go into the tank as embryo stock lots: create the stock item and lot first, then list them under lots.",
    examples: ["flushed donor 50 to Inspiration today, twelve recovered, eight usable, six frozen, two put in fresh"],
  },
});

export interface CollectionData { straws: number; lot_id?: string; code?: string; motility?: number; collector_id?: string; sexed?: "conventional" | "sexed_female" | "sexed_male"; note?: string }

export const semenCollection = defineEvent<CollectionData>({
  type: "genetics.semen_collection",
  label: "Semen collection",
  description: "Semen collected and frozen from one of your own bulls.",
  icon: "straw",
  tone: "genetics",
  review: "always",
  subjects: [{ role: "bull", kind: "animal", label: "Bull", required: true }],
  fields: {
    straws: f.int("Straws frozen", { required: true, min: 0 }),
    code: f.text("Collection code"),
    lot_id: f.ref("Stored as", "stock_lot", { level: "more", filter: { kind: "semen" } }),
    motility: f.number("Motility", { level: "more", min: 0, max: 100, suffix: "%" }),
    collector_id: f.ref("Collected by", "contact", { level: "more" }),
    sexed: f.enum("Sorted", { conventional: "Conventional", sexed_female: "Sexed heifer", sexed_male: "Sexed bull" }, { level: "more" }),
    note: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const bull = subject(e.subjects, "bull")?.label ?? "";
    const base = x.t("Collected {{bull}}: {{straws}}", { bull, straws: x.count(e.data.straws, "{{count}} straw", "{{count}} straws") });
    return e.data.code ? `${base} (${e.data.code})` : base;
  },
  effects: { stock: (d) => (d.lot_id && d.straws ? [{ lot_id: d.lot_id, delta: d.straws, unit: "straw", reason: "receive" }] : []) },
  agent: {
    when: "Semen was collected and frozen from one of the ranch's own bulls.",
    examples: ["collected B12 today, 40 straws, code B12-2026-05"],
  },
});

export interface InventoryMoveData { action: keyof typeof INVENTORY_ACTIONS; quantity?: number; counted?: number; adjust_by?: number; to_tank?: string; to_canister?: string; to_cane?: string; price?: Money; note?: string }

export const inventoryMove = defineEvent<InventoryMoveData>({
  type: "genetics.inventory_move",
  label: "Semen or embryo movement",
  description: "Straws or embryos moved between tanks, sold, shipped, counted or thrown out.",
  icon: "tank",
  tone: "genetics",
  review: "always",
  subjects: [
    { role: "lot", kind: "stock_lot", label: "From lot", required: true },
    { role: "to_lot", kind: "stock_lot", label: "Into lot", help: "When only part of a lot moves to another tank." },
    { role: "party", kind: "contact", label: "Buyer or receiver" },
  ],
  fields: {
    action: f.enum("What happened", INVENTORY_ACTIONS, { required: true }),
    quantity: f.int("How many", { min: 0 }),
    counted: f.int("Counted on hand", { min: 0 }),
    adjust_by: f.int("Change to the book", { level: "more", help: "Counted minus what the book said." }),
    to_tank: f.text("To tank", { level: "more" }),
    to_canister: f.text("To canister", { level: "more" }),
    to_cane: f.text("To cane", { level: "more" }),
    price: f.money("Price", { level: "more" }),
    note: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const lot = subject(e.subjects, "lot")?.label ?? "";
    const party = subject(e.subjects, "party")?.label;
    const q = e.data.quantity ?? 0;
    switch (e.data.action) {
      case "moved": {
        const where = [e.data.to_tank, e.data.to_canister && x.t("canister {{c}}", { c: e.data.to_canister }), e.data.to_cane && x.t("cane {{c}}", { c: e.data.to_cane })].filter(Boolean).join(", ");
        return subject(e.subjects, "to_lot") ? x.t("Moved {{count}} of {{lot}} to {{where}}", { count: q, lot, where }) : x.t("Moved {{lot}} to {{where}}", { lot, where });
      }
      case "sold": return party ? x.t("Sold {{count}} of {{lot}} to {{party}}", { count: q, lot, party }) : x.t("Sold {{count}} of {{lot}}", { count: q, lot });
      case "shipped": return party ? x.t("Shipped {{count}} of {{lot}} to {{party}}", { count: q, lot, party }) : x.t("Shipped {{count}} of {{lot}}", { count: q, lot });
      case "discarded": return x.t("Threw out {{count}} of {{lot}}", { count: q, lot });
      case "returned": return x.t("Got {{count}} of {{lot}} back", { count: q, lot });
      case "counted": return e.data.adjust_by ? x.t("Counted {{counted}} of {{lot}} (book off by {{diff}})", { counted: e.data.counted ?? 0, lot, diff: e.data.adjust_by > 0 ? `+${e.data.adjust_by}` : e.data.adjust_by }) : x.t("Counted {{counted}} of {{lot}}, matches the book", { counted: e.data.counted ?? 0, lot });
    }
    return lot;
  },
  validate: (d, subjects) => {
    const issues: Issue[] = [];
    if (["sold", "shipped", "discarded", "returned"].includes(d.action) && !d.quantity) issues.push({ path: "data.quantity", message: "Say how many." });
    if (d.action === "counted" && d.counted === undefined) issues.push({ path: "data.counted", message: "Say how many you counted." });
    if (d.action === "moved" && subject(subjects, "to_lot") && !d.quantity) issues.push({ path: "data.quantity", message: "Say how many moved." });
    if (d.action === "moved" && !d.to_tank && !d.to_canister && !d.to_cane) issues.push({ path: "data.to_tank", message: "Say where they went." });
    const from = subject(subjects, "lot");
    const to = subject(subjects, "to_lot");
    if (from && to && from.id === to.id) issues.push({ path: "subjects.to_lot", message: "Pick a different lot to move into." });
    return issues;
  },
  effects: {
    stock: (d, subjects) => {
      const lot = subject(subjects, "lot")?.id;
      const to = subject(subjects, "to_lot")?.id;
      const q = d.quantity ?? 0;
      if (!lot) return [];
      switch (d.action) {
        case "moved": return to && q ? [{ lot_id: lot, delta: -q, unit: "each", reason: "transfer" }, { lot_id: to, delta: q, unit: "each", reason: "transfer" }] : [];
        case "sold": return q ? [{ lot_id: lot, delta: -q, unit: "each", reason: "sell" }] : [];
        case "shipped": return q ? [{ lot_id: lot, delta: -q, unit: "each", reason: "transfer" }] : [];
        case "discarded": return q ? [{ lot_id: lot, delta: -q, unit: "each", reason: "waste" }] : [];
        case "returned": return q ? [{ lot_id: lot, delta: q, unit: "each", reason: "receive" }] : [];
        case "counted": return d.adjust_by ? [{ lot_id: lot, delta: d.adjust_by, unit: "each", reason: "count" }] : [];
      }
      return [];
    },
  },
  agent: {
    when: "Semen straws or embryos changed hands or place: moved between tanks, sold, shipped, broken, returned, or counted.",
    avoid: "Using a straw to breed a cow is breeding's AI record. Receiving new semen is a stock lot (create stock_item and stock_lot).",
    examples: ["sold 5 straws of B12 to Bob Jones", "moved the Inspiration semen to tank 2, canister 4", "counted 20 Inspiration straws in the tank"],
  },
});

export const EVENTS = [
  parentage, pedigreeCorrection, breedComposition, evaluation, dnaSample, dnaSent, dnaResult, registration, registrationTransfer, contemporaryGroup, flush, semenCollection, inventoryMove,
];
